// Firebase Configuration for Train Smart AI
// Includes: Authentication + Push Notifications

import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo
} from 'firebase/auth';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import authFetch from './utils/authFetch';
import { decideGoogleAccess, googleErrorMessage, GOOGLE_DENIED_MESSAGES } from './utils/googleAccess';
import { passwordSetupStatus } from './utils/passwordSetup';

// Firebase config
const firebaseConfig = {
  apiKey: "AIzaSyBbCrRz82sndQ1_e_iaNOE0uElhgRYvSOo",
  authDomain: "run-smart-ai-ce009.firebaseapp.com",
  projectId: "run-smart-ai-ce009",
  storageBucket: "run-smart-ai-ce009.firebasestorage.app",
  messagingSenderId: "878635287842",
  appId: "1:878635287842:web:f511f3f960c464c64c917b",
  measurementId: "G-2YSE6V7LW2"
};

const VAPID_KEY = "BC3omVabFLPDHROoGvXCM1fAIry8iX6HSQnRbQ-wjtbUnxtuqlOzUIzzXDkvHBWjN1RHvWQiNKBLSiovDbb74sQ";

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Auth
const auth = getAuth(app);

// Initialize Firestore
const db = getFirestore(app);

// Initialize Messaging (only in browser)
let messaging = null;
if (typeof window !== 'undefined' && 'Notification' in window) {
  try {
    messaging = getMessaging(app);
  } catch (error) {
    console.log('Firebase messaging not supported:', error);
  }
}

// ==================== AUTH FUNCTIONS ====================

// Check if email is in allowed_users (pilot mode)
export async function checkAllowedUser(email) {
  try {
    const docRef = doc(db, 'allowed_users', email.toLowerCase());
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().approved === true) {
      return { allowed: true };
    }
    return { allowed: false };
  } catch (error) {
    console.error('❌ Error checking allowed users:', error);
    return { allowed: false, error: true };   // still not allowed; `error` lets Google sign-in say "try again"
  }
}

// Sign up with email and password
export async function signUp(email, password) {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    console.log('✅ User created:', userCredential.user.uid);
    return { success: true, user: userCredential.user };
  } catch (error) {
    console.error('❌ Sign up error:', error.code, error.message);
    return { success: false, error: getAuthErrorMessage(error.code) };
  }
}

// Sign in with email and password
export async function signIn(email, password) {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    console.log('✅ User signed in:', userCredential.user.uid);
    return { success: true, user: userCredential.user };
  } catch (error) {
    console.error('❌ Sign in error:', error.code, error.message);
    return { success: false, error: getAuthErrorMessage(error.code) };
  }
}

// Sign out
export async function logOut() {
  try {
    await signOut(auth);
    console.log('✅ User signed out');
    return { success: true };
  } catch (error) {
    console.error('❌ Sign out error:', error);
    return { success: false, error: error.message };
  }
}

// Password reset
export async function resetPassword(email) {
  try {
    await sendPasswordResetEmail(auth, email);
    console.log('✅ Password reset email sent');
    return { success: true };
  } catch (error) {
    console.error('❌ Password reset error:', error?.code);
    // neutral: "no such user" looks exactly like a sent email (the screen never says whether an account exists)
    if (error?.code === 'auth/user-not-found') return { success: true };
    return { success: false, error: getAuthErrorMessage(error.code) };
  }
}

// Profile "הגדרת סיסמה" (Option B, 2026-10-21): Firebase's existing password-reset email, sent ONLY for the
// signed-in user and ONLY to that account's own verified email (passwordSetupStatus) — this function takes no
// address. The password is chosen from the email link; the Firebase user is never linked / unlinked / edited here.
export async function sendPasswordSetupEmail() {
  const user = auth.currentUser;
  const status = passwordSetupStatus(user);
  if (!status.eligible) return { success: false, reason: status.reason };
  try {
    await sendPasswordResetEmail(auth, status.email);
    console.log('✅ Password setup email sent');
    return { success: true, email: status.email };
  } catch (error) {
    console.error('❌ Password setup email error:', error?.code);
    return { success: false, reason: error?.code === 'auth/too-many-requests' ? 'rate_limited' : 'failed' };
  }
}

// ==================== GOOGLE SIGN-IN (Phase 1, popup) ====================
// Popup only (GOOGLE_SIGNIN_ENABLED, features.js). Never deletes, links, unlinks or edits a Firebase user and never
// touches profile data.
//
// Per-tab access gate. Firebase shares the signed-in session between tabs of the origin, so EVERY tab verifies a
// Google session with the backend itself before the app (consent / onboarding / profile / plans) ever sees it:
//   • the session is held back from onAuthChange's callback until GET /access/status (+ the allowed_users invite
//     for a user without an access row) is decided by decideGoogleAccess (utils/googleAccess);
//   • the result lives ONLY in this tab's memory — nothing in localStorage / sessionStorage is trusted as proof, so
//     a reload or another tab always checks again;
//   • approved → the callback runs; definitively denied (not invited / pending / blocked) → signed out everywhere;
//     the check unavailable (error / timeout) → this tab stays signed out in the app (callback(null)) without
//     signing the other tabs out; the tab that started the sign-in also signs out (fail closed).
// Sessions without the google.com provider (email / password) are not gated here — unchanged.
const ACCESS_STATUS_TIMEOUT_MS = 30000;     // Render cold start
const ACCESS_STATUS_ATTEMPTS = 2;

const verifiedUids = new Set();             // this tab only, in memory: uids this tab verified as allowed
let pendingCheck = null;                    // { uid, promise } — one access check per uid at a time in this tab
let googleSignInBusy = false;
let pendingDenialMessage = '';
const denialListeners = new Set();

function requiresAccessCheck(user) {
  return (user?.providerData || []).some((p) => p?.providerId === 'google.com');
}

function announceDenial(message) {
  if (!message) return;
  if (denialListeners.size) denialListeners.forEach((fn) => fn(message));
  else pendingDenialMessage = message;      // the login screen is not mounted yet: shown when it mounts
}

// The login screen subscribes to show why a Google session was not let in (also for a session another tab started).
export function subscribeGoogleAccessDenied(fn) {
  if (pendingDenialMessage) { fn(pendingDenialMessage); pendingDenialMessage = ''; }
  denialListeners.add(fn);
  return () => { denialListeners.delete(fn); };
}

// The caller's own closed-pilot status from the backend (GET /access/status), or null when unavailable.
async function fetchAccessStatus() {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';
  for (let attempt = 1; attempt <= ACCESS_STATUS_ATTEMPTS; attempt += 1) {
    try {
      const signal = typeof AbortSignal !== 'undefined' && AbortSignal.timeout
        ? AbortSignal.timeout(ACCESS_STATUS_TIMEOUT_MS) : undefined;
      const res = await authFetch(`${API_BASE_URL}/access/status`, { cache: 'no-store', signal });
      if (res.ok) {
        const data = await res.json();
        return typeof data?.status === 'string' ? data.status : null;
      }
    } catch (error) {
      console.error('❌ Access status check failed:', error?.name || error);
    }
  }
  return null;
}

async function checkGoogleAccess(user) {
  const accessStatus = await fetchAccessStatus();
  const allowlist = accessStatus === 'missing' ? await checkAllowedUser(user.email || '') : null;
  return decideGoogleAccess({ accessStatus, allowlist, emailVerified: user.emailVerified === true });
}

// This tab's verdict for a signed-in Google session (shared by onAuthChange and signInWithGoogle).
function authorizeSession(user) {
  if (verifiedUids.has(user.uid)) return Promise.resolve({ allowed: true });
  if (pendingCheck && pendingCheck.uid === user.uid) return pendingCheck.promise;
  const promise = (async () => {
    let decision;
    try {
      decision = await checkGoogleAccess(user);
    } catch (error) {
      console.error('❌ Google access check failed:', error?.name || error);
      decision = { allowed: false, reason: 'check_failed', message: GOOGLE_DENIED_MESSAGES.check_failed };
    }
    if (decision.allowed) {
      if (auth.currentUser?.uid === user.uid) verifiedUids.add(user.uid);
    } else if (decision.reason !== 'check_failed') {
      console.warn('🚫 Google session denied:', decision.reason);
      if (auth.currentUser?.uid === user.uid) await logOut();
    }
    return decision;
  })();
  pendingCheck = { uid: user.uid, promise };
  promise.finally(() => { if (pendingCheck?.promise === promise) pendingCheck = null; });
  return promise;
}

export async function signInWithGoogle() {
  if (googleSignInBusy) return { success: false, code: 'busy', error: '' };
  googleSignInBusy = true;
  try {
    let credential;
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      credential = await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('❌ Google sign-in error:', error?.code);
      return { success: false, code: error?.code || 'unknown', error: googleErrorMessage(error?.code) };
    }
    const user = credential.user;
    const isNewUser = !!getAdditionalUserInfo(credential)?.isNewUser;
    console.log('🔑 Google sign-in:', user.uid, isNewUser ? '(new Firebase user)' : '(existing Firebase user)',
      (user.providerData || []).map((p) => p.providerId).join(','));
    const decision = await authorizeSession(user);
    if (!decision.allowed) {
      // the tab that asked to sign in never keeps an unverified session (denials already signed out)
      if (auth.currentUser?.uid === user.uid) await logOut();
      return { success: false, code: decision.reason, error: decision.message };
    }
    return { success: true, user, isNewUser };
  } catch (error) {
    console.error('❌ Google sign-in check failed:', error?.name || error);
    await logOut();
    return { success: false, code: 'check_failed', error: GOOGLE_DENIED_MESSAGES.check_failed };
  } finally {
    googleSignInBusy = false;
  }
}

// Listen to auth state changes
export function onAuthChange(callback) {
  return onAuthStateChanged(auth, async (user) => {
    if (!user) {
      verifiedUids.clear();               // a later sign-in (any account) is checked again
      callback(null);
      return;
    }
    if (!requiresAccessCheck(user)) {
      callback(user);                     // email / password: unchanged, immediate
      return;
    }
    const decision = await authorizeSession(user);
    const current = auth.currentUser?.uid;
    if (decision.allowed) {
      if (current === user.uid) callback(user);       // otherwise signed out / switched meanwhile — that event decides
      return;
    }
    if (current && current !== user.uid) return;      // another account signed in meanwhile — not its verdict
    announceDenial(decision.message);
    // denied sessions were signed out (the null event reaches the app); an unavailable check keeps this tab out
    if (decision.reason === 'check_failed' && current === user.uid) callback(null);
  });
}

// Get current user
export function getCurrentUser() {
  return auth.currentUser;
}

// Get auth error message in Hebrew. Neutral (2026-10-20): the messages never tell an unauthenticated visitor whether
// an account exists for an email or how it signs in — unknown user, wrong password and a password removed by a
// Google sign-in all read the same.
const INVALID_CREDENTIALS_MSG = 'לא הצלחנו להתחבר עם הפרטים האלה. בדוק את האימייל והסיסמה ונסה שוב, או אפס סיסמה.';
function getAuthErrorMessage(errorCode) {
  const errors = {
    'auth/email-already-in-use': 'לא ניתן להשלים את ההרשמה עם האימייל הזה.\nאם כבר נרשמת, התחבר או אפס סיסמה.',
    'auth/invalid-email': 'כתובת אימייל לא תקינה',
    'auth/operation-not-allowed': 'פעולה לא מורשית',
    'auth/weak-password': 'הסיסמה חלשה מדי (מינימום 6 תווים)',
    'auth/user-disabled': 'לא ניתן להתחבר כרגע.\nלפרטים: support@ritmoiq.com',
    'auth/user-not-found': INVALID_CREDENTIALS_MSG,
    'auth/wrong-password': INVALID_CREDENTIALS_MSG,
    'auth/invalid-credential': INVALID_CREDENTIALS_MSG,
    'auth/invalid-login-credentials': INVALID_CREDENTIALS_MSG,
    'auth/too-many-requests': 'יותר מדי נסיונות. נסה שוב מאוחר יותר',
  };
  return errors[errorCode] || 'שגיאה לא צפויה. נסה שוב';
}

// ==================== PUSH NOTIFICATION FUNCTIONS ====================

// Request permission and get FCM token
export async function requestNotificationPermission() {
  if (!messaging) {
    console.log('Messaging not available');
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return null;
    }

    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    
    if (token) {
      console.log('FCM Token:', token);
      return token;
    } else {
      console.log('No FCM token available');
      return null;
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
}

// Listen for foreground messages
export function onForegroundMessage(callback) {
  if (!messaging) return;

  onMessage(messaging, (payload) => {
    console.log('Foreground message received:', payload);
    callback(payload);
  });
}

// Register token with backend
export async function registerTokenWithBackend(token, userId) {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

  try {
    const response = await authFetch(`${API_BASE_URL}/register-push-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        fcm_token: token
      })
    });

    if (response.ok) {
      console.log('Token registered with backend');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error registering token:', error);
    return false;
  }
}

// Silently refresh FCM token on every app load — fixes the "worked yesterday but
// not today" problem caused by FCM token rotation. Safe to call repeatedly; does
// nothing unless the user has already enabled notifications (permission granted).
export async function refreshPushTokenIfNeeded(userId) {
  if (!messaging || !userId) return false;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return false;
  }

  try {
    const currentToken = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (!currentToken) {
      console.log('⚠️ Push: permission granted but no FCM token available');
      return false;
    }

    const storedToken = localStorage.getItem('fcm-token');
    // Per-device registration (2026-10-18): the backend used to keep ONE token per user, so the last device to
    // open the app took every push. Each device re-registers once after the fix so the backend knows ALL of them.
    const multiDeviceSynced = localStorage.getItem('fcm-multi-device-v1') === 'true';
    if (currentToken === storedToken && multiDeviceSynced) {
      // Even when unchanged locally, re-send to backend if local flag is missing
      // (e.g., backend lost token but browser still has it).
      const synced = localStorage.getItem('fcm-token-synced-at');
      const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
      if (synced && parseInt(synced, 10) > oneDayAgo) {
        return true;  // Synced within last 24h — trust it
      }
    }

    console.log('🔄 Push: refreshing FCM token with backend');
    const success = await registerTokenWithBackend(currentToken, userId);
    if (success) {
      localStorage.setItem('fcm-token', currentToken);
      localStorage.setItem('push-registered', 'true');
      localStorage.setItem('fcm-token-synced-at', String(Date.now()));
      localStorage.setItem('fcm-multi-device-v1', 'true');
      console.log('✅ Push: FCM token synced with backend');
    }
    return success;
  } catch (error) {
    console.error('❌ Push: token refresh failed:', error);
    return false;
  }
}

// This device's FCM token WITHOUT asking for permission (null unless permission is already granted).
export async function getCurrentPushToken() {
  if (!messaging || typeof Notification === 'undefined' || Notification.permission !== 'granted') return null;
  try {
    return (await getToken(messaging, { vapidKey: VAPID_KEY })) || null;
  } catch {
    return null;
  }
}

// Deliberate user action ONLY (a button): ask the OS permission, then register this device.
// Returns { status: 'registered' | 'denied' | 'dismissed' | 'unsupported' | 'no_token' | 'register_failed', token }
export async function enablePushOnThisDevice(userId) {
  if (!messaging || typeof Notification === 'undefined') return { status: 'unsupported', token: null };
  let permission;
  try {
    permission = await Notification.requestPermission();
  } catch {
    return { status: 'unsupported', token: null };
  }
  if (permission === 'denied') return { status: 'denied', token: null };
  if (permission !== 'granted') return { status: 'dismissed', token: null };
  let token = null;
  try {
    token = await getToken(messaging, { vapidKey: VAPID_KEY });
  } catch {
    token = null;
  }
  if (!token) return { status: 'no_token', token: null };
  const ok = await registerTokenWithBackend(token, userId);   // upsert on the token: never a duplicate device
  if (!ok) return { status: 'register_failed', token };
  try {
    localStorage.setItem('fcm-token', token);
    localStorage.setItem('push-registered', 'true');
    localStorage.setItem('fcm-token-synced-at', String(Date.now()));
    localStorage.setItem('fcm-multi-device-v1', 'true');
  } catch { /* storage unavailable */ }
  return { status: 'registered', token };
}

// Export instances
export { auth, db, messaging };
